import { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';
import { Flex, Heading, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';

import { getValidRowsWithDates } from '../../services/grid';
import {
    getLoadDataChart,
    createLoadData,
    getLoadData,
    updateLoadData,
    deleteLoadData,
    deleteAllLoadData,
    formatParsedNewLoadData,
    getLoadForecastChart,
    createLoadForecasts,
    getLoadForecast,
    updateLoadForecast,
    deleteLoadForecasts,
    deleteAllLoadForecasts,
    formatParsedNewLoadForecast,
    createLoadModels,
    getLoadModel,
    updateLoadModel,
    deleteLoadModels,
    deleteAllLoadModels,
    formatParsedNewLoadModel,
} from '../../services/loads';

import SubItemData from '../itemData/SubItemData';
import DownloadButton from '../itemData/DownloadButton';
import UploadButton from '../itemData/UploadButton';

import Combobox from '../forms/Combobox';

import SecondaryButton from '../utils/SecondaryButton';

import { ReactComponent as RefreshIcon } from '../../icons/refresh.svg';

const SubLoadData = ({ itemLabel, subLoadId, itemId, filters }) => {
    const forecastTypes = useSelector((state) => state.forecast.types);
    const intl = useIntl();

    const [key, setKey] = useState(0);
    const [loadingQueue, setLoadingQueue] = useState([]); // unique keys represeting which resource is in loading state
    const [forecastType, setForecastType] = useState(122);

    const refreshTabs = useCallback(() => setKey((prev) => prev + 1), []);

    const onLoadingStart = useCallback((key) => {
        setLoadingQueue((prev) => prev.concat(key));
    }, []);

    const onLoadingEnd = useCallback((key) => {
        setLoadingQueue((prev) => prev.filter((p) => p !== key));
    }, []);

    const loadDataChartResource = useMemo(() => {
        return {
            read: (pagination) => {
                const payload = {
                    pointCount: pagination.pointCount,
                    startDateFrom: pagination.startDateFrom || filters.startDateFrom,
                    startDateTo: pagination.startDateTo || filters.startDateTo,
                    updateDateFrom: filters.updateDateFrom,
                    updateDateTo: filters.updateDateTo,
                    filterType: filters.filterType,
                };

                return getLoadDataChart(subLoadId, payload);
            },
        };
    }, [subLoadId, filters]);

    const loadDataGridResource = useMemo(() => {
        return {
            create: (rows) => createLoadData(subLoadId, rows),
            read: (pagination) => getLoadData(subLoadId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateLoadData(subLoadId, validRows);
            },
            delete: (rows) =>
                deleteLoadData(
                    subLoadId,
                    rows.map((row) => row.loadDataId)
                ),
            deleteAll: () => deleteAllLoadData(subLoadId),
        };
    }, [subLoadId, filters]);

    const loadDataColumnSchema = useMemo(() => {
        return {
            startDate: { name: 'startDate', type: 'date' },
            endDate: { name: 'endDate', type: 'date' },
            load: { name: 'load', type: 'number' },
            updateDateTime: { name: 'updateDateTime', type: 'date' },
        };
    }, []);

    const loadForecastChartResource = useMemo(() => {
        return {
            read: (pagination) => {
                const payload = {
                    pointCount: pagination.pointCount,
                    startDateFrom: pagination.startDateFrom || filters.startDateFrom,
                    startDateTo: pagination.startDateTo || filters.startDateTo,
                    updateDateFrom: filters.updateDateFrom,
                    updateDateTo: filters.updateDateTo,
                    filterType: filters.filterType,
                    forecastTypeId: forecastType,
                };

                return getLoadForecastChart(subLoadId, payload);
            },
        };
    }, [subLoadId, filters, forecastType]);

    const loadForecastGridResource = useMemo(() => {
        return {
            create: (rows) => createLoadForecasts(subLoadId, rows),
            read: (pagination) => getLoadForecast(subLoadId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateLoadForecast(subLoadId, validRows);
            },
            delete: (rows) =>
                deleteLoadForecasts(
                    subLoadId,
                    rows.map((row) => row.loadForecastId)
                ),
            deleteAll: () => deleteAllLoadForecasts(subLoadId),
        };
    }, [subLoadId, filters]);

    const loadForecastColumnSchema = useMemo(() => {
        return {
            forecastTypeId: {
                name: 'forecastTypeId',
                type: 'string',
                isRequired: true,
                constraints: forecastTypes.map((type) => type.description),
            },
            startDate: { name: 'startDate', type: 'date' },
            endDate: { name: 'endDate', type: 'date' },
            expectedEnergy: { name: 'expectedEnergy', type: 'number' },
            expectedPeakDemand: { name: 'expectedPeakDemand', type: 'number' },
            stdDevOfEnergy: { name: 'stdDevOfEnergy', type: 'number' },
            updateDateTime: { name: 'updateDateTime', type: 'date' },
        };
    }, [forecastTypes]);

    const formatNewLoadForecast = useCallback(
        (data) => formatParsedNewLoadForecast(data, forecastTypes),
        [forecastTypes]
    );

    const loadModelGridResource = useMemo(() => {
        return {
            create: (rows) => createLoadModels(subLoadId, rows),
            read: () => getLoadModel(subLoadId),
            update: (rows) => updateLoadModel(subLoadId, rows),
            delete: (rows) =>
                deleteLoadModels(
                    subLoadId,
                    rows.map((row) => row.loadModelId)
                ),
            deleteAll: () => deleteAllLoadModels(subLoadId),
        };
    }, [subLoadId]);

    const loadModelColumnSchema = useMemo(() => {
        return {
            useFlag: { name: 'useFlag', isRequired: true, type: 'bool-number' },
            description: { name: 'description', type: 'string' },
            formulaStatement: { name: 'formulaStatement', type: 'string' },
            runOrder: { name: 'runOrder', type: 'number' },
        };
    }, []);

    const loadDataUnitLabel = intl.formatMessage({ id: 'common_item_unit_label' }, { item: 'Load', unit: 'MWh' });

    return (
        <Tabs px={0} mt={6} isLazy isManual lazyBehavior="keepMounted" key={key}>
            <StyledTabList w="100%" flexWrap={{ base: 'wrap', xl: 'nowrap' }} alignItems="center">
                <StyledTab>
                    <FormattedMessage id="common_historical_data_data" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="common_historical_data_forecast" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="common_historical_data_model" />
                </StyledTab>

                <Flex ml="auto" order={{ base: -1, xl: 0 }} w={{ base: '100%', xl: 'auto' }}>
                    <DownloadButton
                        type="loads"
                        itemId={itemId}
                        filters={filters}
                        hasLabel={true}
                        itemType="load"
                        mr={2}
                        w="auto !important"
                    />

                    <UploadButton type="loads" itemId={itemId} hasLabel={true} mr={4} w="auto !important" />

                    <SecondaryButton
                        onClick={refreshTabs}
                        size="sm"
                        variant="secondary"
                        leftIcon={<RefreshIcon />}
                        isLoading={loadingQueue.length > 0}
                        loadingText={intl.formatMessage({ id: 'common_loading' })}
                        textTransform="capitalize"
                        ml="auto"
                    >
                        <FormattedMessage id="common_refresh" />
                    </SecondaryButton>
                </Flex>
            </StyledTabList>

            <TabPanels mt={4}>
                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_historical_data_heading" values={{ item: 'Load' }} />
                    </Heading>

                    <SubItemData
                        hasChart
                        tab="0"
                        yAxisLabel={loadDataUnitLabel}
                        chartResource={loadDataChartResource}
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={loadDataGridResource}
                        columns={[
                            {
                                type: 'date',
                                field: 'startDate',
                                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                sortable: true,
                                sort: 'asc',
                            },
                            {
                                type: 'date',
                                field: 'endDate',
                                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                            },
                            {
                                field: 'load',
                                type: 'number',
                                headerName: loadDataUnitLabel,
                            },
                            {
                                type: 'date',
                                field: 'updateDateTime',
                                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                                sortable: true,
                                sort: 'asc',
                            },
                        ]}
                        addRowsValidationSchema={loadDataColumnSchema}
                        formatNewRows={formatParsedNewLoadData}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_historical_data_forecast_heading" values={{ item: 'Load' }} />
                    </Heading>

                    <SubItemData
                        hasChart
                        actionBarSlot={(index) =>
                            index === 0 ? (
                                <Combobox
                                    width="var(--chakra-sizes-2xs) !important"
                                    options={forecastTypes}
                                    valueKey="id"
                                    labelKey="description"
                                    isControlled={false}
                                    onChange={setForecastType}
                                />
                            ) : null
                        }
                        tab="1"
                        yAxisLabel={loadDataUnitLabel}
                        chartResource={loadForecastChartResource}
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={loadForecastGridResource}
                        columns={[
                            {
                                field: 'forecastTypeId',
                                type: 'select',
                                cellEditorParams: { options: forecastTypes },
                                headerName: intl.formatMessage({ id: 'common_grid_forecast_type' }),
                            },
                            {
                                type: 'date',
                                field: 'startDate',
                                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                sortable: true,
                                sort: 'asc',
                            },
                            {
                                type: 'date',
                                field: 'endDate',
                                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                            },
                            {
                                field: 'expectedEnergy',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'Expected Energy', unit: 'MWh' }
                                ),
                            },
                            {
                                field: 'expectedPeakDemand',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'Expected Peak Demand', unit: 'MW' }
                                ),
                            },
                            {
                                field: 'stdDevOfEnergy',
                                type: 'number',
                                headerName: intl.formatMessage({ id: 'common_grid_std_dev_of_energy' }),
                            },
                            {
                                type: 'date',
                                field: 'updateDateTime',
                                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                                sortable: true,
                                sort: 'asc',
                            },
                        ]}
                        addRowsValidationSchema={loadForecastColumnSchema}
                        formatNewRows={formatNewLoadForecast}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_historical_data_model_heading" values={{ item: 'Load' }} />
                    </Heading>

                    <SubItemData
                        tab="2"
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={loadModelGridResource}
                        columns={[
                            {
                                type: 'checkbox',
                                field: 'useFlag',
                                headerName: intl.formatMessage({ id: 'common_grid_model_use' }),
                            },
                            {
                                field: 'description',
                                headerName: intl.formatMessage({ id: 'common_grid_model_description' }),
                            },
                            {
                                field: 'formulaStatement',
                                headerName: intl.formatMessage({ id: 'common_grid_model_formula' }),
                            },
                            {
                                field: 'runOrder',
                                type: 'number',
                                headerName: intl.formatMessage({ id: 'common_grid_model_run_order' }),
                            },
                        ]}
                        addRowsValidationSchema={loadModelColumnSchema}
                        formatNewRows={formatParsedNewLoadModel}
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>
    );
};

// @todo duplicate styles in SubLoadData/SubRenewableData/SubBatteryData
const StyledTabList = styled(TabList)`
    border-bottom: 1px solid var(--chakra-colors-gray-200);

    > [aria-selected='true'] {
        border-bottom: 4px solid var(--chakra-colors-blue-400);
        color: var(--chakra-colors-black);
    }

    > [role='tab'] {
        color: var(--chakra-colors-black);

        &:hover {
            color: var(--chakra-colors-blue-400);
        }
    }
`;

const StyledTab = styled(Tab)`
    padding: 16px 0;
    margin-right: 24px;
    margin-bottom: 0;
    color: var(--chakra-colors-gray-600);
    text-transform: capitalize;
`;

export default SubLoadData;
